import React, { useState, useRef, useEffect } from 'react';
import MonacoEditor from '@monaco-editor/react';
import CompletionPopup from '../../completion-popup';
import { serverURL } from '../../../config/urls';
import { logEvent } from '@firebase/analytics';
import {analytics} from '../../../config/firebase-logging';
import { useCareerContext } from '../../career-context';

function CalculatorExercise() {
    const {
        isPopupOpen,
        setIsPopupOpen,
        startConfetti
    } = useCareerContext();
    const options = { scrollbar: { alwaysConsumeMouseWheel: false, }, scrollBeyondLastLine: false, minimap: { enabled: false, } }
    const inputRef = useRef(null);
    const problem1 = 'def handle_input(instruction, button_pressed):\n\t# Possible values for button_pressed:\n\t# "Clear", "↩", "/", "*", "-", "+", and digits 0-9\n\t# Write code here';
    const problem2 = 'def execute(instruction):\n\t# Write code here';

    const [exerciseStep, setExerciseStep] = useState(1);
    const [readyToMoveOn, setReadyToMoveOn] = useState(false);
    const [showError, setShowError] = useState(false);
    const [code, setCode] = useState(problem1);
    const [output, setOutput] = useState("");
    const [calculatorInput, setCalculatorInput] = useState("");
    const [scrollDirection, setScrollDirection] = useState("right");

    function handleEditorChange(value, event) {
        setCode(value);
    }

    // Scroll calculator input so recent digit is visible
    useEffect(() => {
        const inputElement = inputRef.current;
        if (scrollDirection === "right") {
            inputElement.scrollLeft = inputElement.scrollWidth;
        } else {
            inputElement.scrollLeft = 0;
        }
    }, [calculatorInput, scrollDirection]);

    // Calculate calculator input
    function handleCalculation() {
        if (exerciseStep === 1 || !readyToMoveOn) {
            return;
        }
        const parsedInstructions = calculatorInput.replace("×", "*").replace("÷", "/");
        const [operand1, operand2] = parsedInstructions.split(/[+\-*/]/);
        const num1 = parseFloat(operand1);
        const num2 = parseFloat(operand2);

        if (parsedInstructions.includes("*")) {
            setCalculatorInput((num1 * num2).toString());
        } else if (parsedInstructions.includes("/")) {
            setCalculatorInput((num1 / num2).toString());
        } else if (parsedInstructions.includes("+")) {
            setCalculatorInput((num1 + num2).toString());
        } else if (parsedInstructions.includes("-")) {
            setCalculatorInput((num1 - num2).toString());
        }
    }

    // Handle calculator button presses
    function onKeyPress(e) {
        e.preventDefault();
        if (exerciseStep === 1 && !readyToMoveOn) {
            return;
        }
        const pressedKey = e.target.textContent;
        const operators = ["÷", "×", "-", "+"];
        let instruction = calculatorInput;

        if (pressedKey === "Clear") {
            setCalculatorInput("");
        }
        else if (pressedKey === "↩") {
            if (instruction.length > 0) {
                setCalculatorInput(instruction.slice(0, -1));
            }
        }
        else if (operators.includes(pressedKey)) {
            // operator must follow a number, must not be more than one operator in one instruction
            if (instruction.length > 0 && (!operators.includes(instruction.slice(0, -1))) && !operators.some((operator) => instruction.includes(operator))) {
                setScrollDirection("right");
                setCalculatorInput(instruction + pressedKey);
            }
        }
        else if (pressedKey === "=") {
            if (operators.some((operator) => instruction.includes(operator))) {
                setScrollDirection("left");
                handleCalculation();
            }
        }
        else if (pressedKey === "0") {
            if (instruction.length > 0 && !operators.includes(instruction.slice(-1))) {
                setScrollDirection("right");
                setCalculatorInput(instruction + pressedKey);
            }
        } else {
            setScrollDirection("right");
            setCalculatorInput(instruction + pressedKey);
        }
    }

    async function runCode() {
        if (process.env.REACT_APP_ENVIRONMENT === "production") {
            logEvent(analytics, 'career_interaction')
        }
        const requestOptions = {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code }),
        };
        const response = await fetch(serverURL + (exerciseStep === 1 ? "/handle_input" : "/execute_instruction"), requestOptions);
        const result = await response.json();
        if (result.error) {
            setShowError(true);
            setOutput(result.error);
        } else if (result.verdict === "Fail") {
            setShowError(true);
            if (exerciseStep === 1)
                setOutput(`Test handle_input("${result.test[0]}", "${result.test[1]}") failed.\nExpected: "${result.expected}", got "${result.result}" instead`)
            else
                setOutput(`Test handle_input("${result.test}") failed.\nExpected: ${result.expected}, got ${result.result} instead`)

        } else {
            setOutput("");
            setShowError(false);
            setIsPopupOpen(true);
            setReadyToMoveOn(true);
            if (exerciseStep === 2) {
                startConfetti();
            }
        }
    }
    return (
        <>
            {isPopupOpen && <CompletionPopup title="Success!" message={"Great job, you passed all tests! " + (exerciseStep === 1 ? "Try your code out by entering valid and invalid buttons in the calculator." : "Try out the calculator that you built!")}  hidePopup={() => setIsPopupOpen(false)} />}

            <div className='calculator-exercise-container'>
                <div className='row' style={{justifyContent: "space-between", height: "calc(100% - 45px)"}}>
                    <div className='instructions' style={{ width: "33%", justifyContent: "space-between" }}>
                        {(exerciseStep === 1) ? (<><h2>Building A Functional Calculator</h2>
                            <p>In this project, you will be building a fully working calculator. The interface of the calculator has been built for you, so you will be handling all the logic behind-the-scenes.</p>
                            <h4 style={{ margin: "0px" }}>Instructions</h4>
                            <p>Implement the <code>handle_input(instruction, button_pressed)</code> function, which runs when the user clicks a button on the calculator. The <code>instruction</code> parameter is a string representing the calculators current instructions. <code>button_pressed</code> is another string that represents what button ("Clear", "9", "+", etc.) was just pressed. You will modify <code>instruction</code> considering the following guidelines:</p>
                            <ul>
                                <li>The "Clear" button should set instruction to an empty string</li>
                                <li>The "↩" button should remove the last character from instruction, if the instruction is not empty</li>
                                <li>Operators (+, -, ÷, ×) can be added to the instruction if the instruction isn't empty, ends with a number, and currently doesn't have an operator in it (for our calculator, "5+5-2" is not valid)</li>
                                <li>There cannot be leading 0s (45 is valid, but not 045)</li>
                                <li>You don't need to account for the user pressing the "=" sign</li>
                            </ul>
                            <p>Return the modified instruction. If an invalid button is pressed, return the original instruction.</p>
                            <h4 style={{ margin: "0px" }}>Examples</h4>
                            <p style={{ margin: "5px 0px" }}><code>handle_input(<span className="string">"45+"</span>, <span className='string'>"Clear"</span>)</code> returns <code><span className='string'>""</span></code></p>
                            <p style={{ margin: "5px 0px" }}><code>handle_input(<span className="string">"435"</span>, <span className='string'>"↩"</span>)</code> returns <code><span className='string'>"43"</span></code></p>
                            <p style={{ margin: "5px 0px" }}><code>handle_input(<span className="string">""</span>, <span className='string'>"↩"</span>)</code> returns <code><span className='string'>""</span></code></p>
                            <p style={{ margin: "5px 0px" }}><code>handle_input(<span className="string">"256"</span>, <span className='string'>"*"</span>)</code> returns <code><span className='string'>"256*"</span></code></p>
                            <p style={{ margin: "5px 0px" }}><code>handle_input(<span className="string">"902+"</span>, <span className='string'>"-"</span>)</code> returns <code><span className='string'>"902+"</span></code> because there cannot be two operators</p>
                            <h4 style={{ margin: "0px" }}>Hints</h4>
                            <ul>
                                <li>Checkout the <code>in</code> operator <a href='https://www.askpython.com/python/examples/in-and-not-in-operators-in-python' target="_blank" rel="noreferrer">here</a></li>
                                <li>Consider an if statement to handle every possible <code>button_pressed</code> value. In each of those if statements handle the cases where the <code>button_pressed</code> is valid or invalid</li>
                            </ul></>) :
                            (<>
                                <h2>Making the Calculation</h2>
                                <p>Great job! Now that a you've made sure a user can only enter valid instructions, it's time to actually execute the instructions.</p>
                                <h4 style={{ margin: "0px" }}>Instructions</h4>
                                <p>Implement the <code>execute(instruction)</code> function. You will be given <code>instruction</code>, a string containing the valid calculation the user wants to make when the equal sign is pressed. Return the result as an number, not a string.</p>
                                <h4 style={{ margin: "0px" }}>Examples</h4>
                                <p style={{ margin: "5px 0px" }}><code>execute(<span className="string">"45+2"</span>)</code> returns <code><span className='number'>47</span></code></p>
                                <p style={{ margin: "5px 0px" }}><code>execute(<span className="string">"5*5"</span>)</code> returns <code><span className='number'>25</span></code></p>
                                <p style={{ margin: "5px 0px" }}><code>execute(<span className="string">"5/2"</span>)</code> returns <code><span className='number'>2.5</span></code></p>
                                <h4 style={{ margin: "0px" }}>Hints</h4>
                                <ul>
                                    <li>The instruction always follows the format "number-operator-number".</li>
                                    <li>Once you have extracted the numbers and operators, you will need to use the <code>int()</code> function to convert the numbers from strings to integers. <code>int(<span className='string'>"45"</span>)</code> returns <code>45</code></li>
                                </ul>
                            </>)}


                    </div>
                    <div className='code-editor' style={{ width: "33%" }}>
                        <div className='editor-padding'></div>
                        <MonacoEditor theme='vs-dark' language='python' height="calc(100% - 45px)" value={code} onChange={handleEditorChange} options={options} />
                        <div className="row" style={{ background: "#2a2d36", justifyContent: "flex-end", alignItems: "center", height: "45px", padding: "10px" }}>
                            <button style={{ margin: "0px" }} className='primary-btn' onClick={runCode}>Run Code</button>
                        </div>
                    </div>
                    <div className='project col' style={{ width: "33%", display: "grid", placeContent: "center" }}>
                        {!showError ?
                            (<div className='calculator' style={{ width: "250px" }}>
                                <form className='col'>
                                    <input type='text' className='calculator-screen' value={calculatorInput} disabled ref={inputRef} />
                                    <div className='calculator-buttons'>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td colSpan={2}><button className='calculator-button green' style={{ width: "95%" }} onClick={onKeyPress}>Clear</button></td>
                                                    <td><button className='calculator-button green' onClick={onKeyPress}>↩</button></td>
                                                    <td><button className='calculator-button green' style={{ fontSize: "25px" }} onClick={onKeyPress}>÷</button></td>
                                                </tr>
                                                <tr>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>7</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>8</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>9</button></td>
                                                    <td><button className='calculator-button red' onClick={onKeyPress}>&times;</button></td>
                                                </tr>
                                                <tr>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>4</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>5</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>6</button></td>
                                                    <td><button className='calculator-button red' style={{ fontSize: "30px" }} onClick={onKeyPress}>-</button></td>
                                                </tr>
                                                <tr>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>1</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>2</button></td>
                                                    <td><button className='calculator-button' onClick={onKeyPress}>3</button></td>
                                                    <td><button className='calculator-button red' onClick={onKeyPress}>+</button></td>
                                                </tr>
                                                <tr>
                                                    <td colSpan={3}><button className='calculator-button' style={{ width: "95%" }} onClick={onKeyPress}>0</button></td>
                                                    <td><button className='calculator-button red' onClick={onKeyPress}>=</button></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </form>
                            </div>) : (
                                <pre className='output' style={{ width: "100%", height: "100%", resize: "none", color: "black", border: "none", background: "transparent" }} >{output}</pre>
                            )}
                    </div>

                </div>
                <div className="row" style={{ justifyContent: "flex-end", background: "#2a2d36", height: "45px", paddingRight: "10px" }}>
                    {(exerciseStep === 1) && <button style={{ marginTop: "auto", marginBottom: "auto" }} className={readyToMoveOn ? 'primary-btn' : 'secondary-btn'} onClick={() => {setExerciseStep(2); setCode(problem2); setReadyToMoveOn(false)}}>Next</button>}
                    {(exerciseStep === 2) && <button style={{ marginTop: "auto", marginBottom: "auto" }} className='secondary-btn' onClick={() => {setExerciseStep(1); setCode(problem1); setReadyToMoveOn(false)}}>Back</button>}

                </div>
            </div>
        </>
    );
}

export default CalculatorExercise;